import { useState, useEffect, useCallback, useRef } from 'react';
import { Send, Plus, ChevronRight } from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { T } from '../lib/tokens';
import { primaryRole } from '../lib/rbac';

interface Props { user: AuthUser }

interface Session {
  id:string; user_id:string; role:string; school_id:string|null;
  locale:'ms'|'en'; started_at:string; last_message_at:string;
  message_count:number; expires_at:string;
}
interface PersistedMessage {
  id:string; session_id:string; role:'user'|'assistant'|'tool';
  content:string|null; tool_name:string|null; tool_input:string|null;
  tool_output_preview:string|null; tokens_used:number; created_at:string;
  user_feedback:'POSITIVE'|'NEGATIVE'|null; is_flagged:boolean;
}
interface SendResponse {
  assistantText:string; toolCalls:unknown[];
  tokensUsed:number; usage:{ hourly:number; daily:number; hourlyLimit:number; dailyLimit:number };
  sessionMessageCount:number;
}
interface Usage { hourly:number; daily:number; hourlyLimit:number; dailyLimit:number }

const C = T.color;

// Role-specific suggested questions
const SUGGESTIONS: Record<string, { ms:string; en:string }[]> = {
  teacher: [
    { ms:'Pelajar mana yang selalu lewat minggu ini?', en:"Which students have been consistently late this week?" },
    { ms:'Tunjukkan kehadiran kelas T4A semalam.',    en:'Show attendance for class 4A yesterday.' },
    { ms:'Apakah gred terbaik dalam Matematik?',      en:'What are the top grades in Mathematics?' },
    { ms:'Jadual saya esok.',                         en:'Show my timetable for tomorrow.' },
  ],
  admin: [
    { ms:'Kelas mana yang paling rendah kehadiran bulan ini?', en:'Which class has the lowest attendance this month?' },
    { ms:'Senaraikan permohonan enrolmen yang belum diproses.', en:'List unprocessed enrolment applications.' },
    { ms:'Berapa ramai pelajar di sekolah ini?',               en:'How many students are enrolled in this school?' },
    { ms:'Tunjukkan pendidik yang CPD kurang dari 50 jam.',    en:'Show educators with fewer than 50 CPD hours.' },
  ],
  parent: [
    { ms:'Apakah gred anak saya dalam Matematik?', en:"What is my child's grade in Mathematics?" },
    { ms:'Berapa kali anak saya tidak hadir bulan ini?', en:'How many times did my child miss school this month?' },
    { ms:'Apakah jadual anak saya esok?',          en:"What is my child's schedule tomorrow?" },
    { ms:'Ada pemberitahuan untuk saya?',          en:'Are there any notifications for me?' },
  ],
  moe_admin: [
    { ms:'Tunjukkan kadar kehadiran nasional minggu ini.',     en:'Show national attendance rates for this week.' },
    { ms:'Daerah mana yang paling rendah kehadiran?',         en:'Which district has the lowest attendance rate?' },
    { ms:'Jumlah pelajar yang didaftarkan mengikut daerah.',  en:'Total enrolled students by district.' },
    { ms:'Senaraikan sekolah dengan isu pematuhan DPE.',      en:'List schools with DPE compliance issues.' },
  ],
  dpe_officer: [
    { ms:'Sekolah swasta mana yang lesen akan tamat 90 hari?', en:'Which private schools have licenses expiring in 90 days?' },
    { ms:'Tunjukkan sekolah yang gagal pemeriksaan terakhir.', en:'Show schools that failed their last inspection.' },
    { ms:'Senaraikan kelulusan guru yang masih tertangguh.',   en:'List pending teacher approvals.' },
  ],
  default: [
    { ms:'Berapa ramai pelajar di sekolah ini?',         en:'How many students are in this school?' },
    { ms:'Tunjukkan statistik kehadiran bulan ini.',     en:"Show this month's attendance statistics." },
    { ms:'Apakah laporan yang boleh saya jana?',         en:'What reports can I generate?' },
    { ms:'Siapakah guru-guru di sekolah ini?',           en:'Who are the teachers in this school?' },
  ],
};

export function EduBotPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang==='en';
  const role = primaryRole(user);
  usePageTitle(E?'EduBot AI':'EduBot AI');

  const [sessions,      setSessions]      = useState<Session[]>([]);
  const [activeSession, setActiveSession] = useState<Session|null>(null);
  const [messages,      setMessages]      = useState<PersistedMessage[]>([]);
  const [input,         setInput]         = useState('');
  const [sending,       setSending]       = useState(false);
  const [starting,      setStarting]      = useState(false);
  const [error,         setError]         = useState<string|null>(null);
  const [usage,         setUsage]         = useState<Usage|null>(null);
  const [locale,        setLocale]        = useState<'ms'|'en'>(()=>lang==='en'?'en':'ms');

  const scrollRef = useRef<HTMLDivElement>(null);
  const textRef   = useRef<HTMLTextAreaElement>(null);

  // Sync locale with global lang toggle
  useEffect(()=>{ setLocale(lang==='en'?'en':'ms'); },[lang]);

  const suggestions = SUGGESTIONS[role] ?? SUGGESTIONS['default']!;

  const loadSessions = useCallback(async()=>{
    try { setSessions(await apiGet<Session[]>('/api/edubot/sessions')); }
    catch{ /* non-fatal */ }
  },[]);

  const loadUsage = useCallback(async()=>{
    try { setUsage(await apiGet<Usage>('/api/edubot/usage')); }
    catch{ /* non-fatal */ }
  },[]);

  useEffect(()=>{ void loadSessions(); void loadUsage(); },[loadSessions, loadUsage]);

  useEffect(()=>{
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  },[messages, sending]);

  async function openSession(s:Session) {
    setActiveSession(s); setMessages([]); setError(null);
    try {
      const res = await apiGet<{session:Session;messages:PersistedMessage[]}>(`/api/edubot/sessions/${s.id}`);
      setActiveSession(res.session); setMessages(res.messages); setLocale(res.session.locale);
    } catch(e){ setError(e instanceof Error?e.message:'Error'); }
  }

  async function startNewSession() {
    setStarting(true); setError(null);
    try {
      const s = await apiPost<Session>('/api/edubot/sessions',{ locale });
      setActiveSession(s); setMessages([]); setSessions(p=>[s,...p]);
    } catch(e){ setError(e instanceof Error?e.message:'Error'); }
    finally { setStarting(false); }
  }

  async function handleSend(text?:string) {
    const msg = (text??input).trim();
    if (!msg || !activeSession || sending) return;

    const optimistic: PersistedMessage = {
      id:`opt-${Date.now()}`, session_id:activeSession.id, role:'user',
      content:msg, tool_name:null, tool_input:null, tool_output_preview:null,
      tokens_used:0, created_at:new Date().toISOString(),
      user_feedback:null, is_flagged:false,
    };
    setMessages(p=>[...p,optimistic]);
    setInput(''); setSending(true); setError(null);

    try {
      const res = await apiPost<SendResponse>(`/api/edubot/sessions/${activeSession.id}/messages`,{ message:msg, locale });
      const refreshed = await apiGet<{session:Session;messages:PersistedMessage[]}>(`/api/edubot/sessions/${activeSession.id}`);
      setActiveSession(refreshed.session); setMessages(refreshed.messages); setUsage(res.usage);
    } catch(e){
      setMessages(p=>p.filter(m=>m.id!==optimistic.id));
      setError(e instanceof Error?e.message:'Error');
    } finally { setSending(false); }
  }

  async function handleFeedback(id:string, fb:'POSITIVE'|'NEGATIVE') {
    try {
      await apiPost(`/api/edubot/messages/${id}/feedback`,{ feedback:fb });
      setMessages(p=>p.map(m=>m.id===id?{...m,user_feedback:fb}:m));
    } catch{ /* non-fatal */ }
  }

  const today = new Date().toDateString();

  // Group sessions by day
  const todaySessions  = sessions.filter(s=>new Date(s.started_at).toDateString()===today);
  const olderSessions  = sessions.filter(s=>new Date(s.started_at).toDateString()!==today);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{ display:'grid', gridTemplateColumns:'280px 1fr', gap:'0', height:'calc(100vh - 92px)', background:C.card, borderRadius:'12px', border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:T.shadow.md }}>

      {/* ── Left sidebar: sessions ── */}
      <div style={{ background:C.midnight, display:'flex', flexDirection:'column', borderRight:`1px solid rgba(255,255,255,0.06)`, overflow:'hidden' }}>
        {/* Header */}
        <div style={{ padding:'16px', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'12px' }}>
            <div style={{ width:'32px', height:'32px', borderRadius:'8px', background:C.royal, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <span style={{ fontFamily:T.font.serif, fontSize:'14px', fontWeight:700, color:C.midnight }}>AI</span>
            </div>
            <div>
              <p style={{ fontFamily:T.font.serif, fontSize:'14px', fontWeight:700, color:'#F9FAFB', lineHeight:1.2 }}>EduBot</p>
              <p style={{ fontSize:'10px', color:'#4A5568', lineHeight:1 }}>{E?'SERPS AI Assistant':'Pembantu AI SERPS'}</p>
            </div>
          </div>

          {/* Locale selector */}
          <div style={{ display:'flex', background:'#1E2128', borderRadius:'6px', padding:'2px', marginBottom:'10px' }}>
            {(['ms','en'] as const).map(l=>(
              <button
                key={l}
                onClick={()=>setLocale(l)}
                style={{
                  flex:1, padding:'5px', background:locale===l?C.royal:'transparent',
                  color:locale===l?C.midnight:'#4A5568',
                  border:'none', borderRadius:'4px',
                  fontSize:'11px', fontWeight:locale===l?700:500,
                  cursor:'pointer', transition:'all 0.1s',
                  fontFamily:T.font.sans, textTransform:'uppercase', letterSpacing:'0.04em',
                }}
              >
                {l==='ms'?'BM':'EN'}
              </button>
            ))}
          </div>

          <button
            onClick={()=>void startNewSession()}
            disabled={starting}
            style={{
              width:'100%', background:C.royal, color:C.midnight,
              border:'none', borderRadius:'7px',
              padding:'9px 12px', fontSize:'12px', fontWeight:700,
              cursor:starting?'not-allowed':'pointer',
              fontFamily:T.font.sans,
              display:'flex', alignItems:'center', justifyContent:'center', gap:'6px',
              opacity:starting?0.8:1,
            }}
          >
            <Plus size={13}/>{starting?(E?'Starting…':'Memulakan…'):(E?'New Session':'Sesi Baharu')}
          </button>
        </div>

        {/* Session list */}
        <div style={{ flex:1, overflowY:'auto', padding:'8px' }}>
          {sessions.length===0 && (
            <p style={{ fontSize:'12px', color:'#4A5568', textAlign:'center', padding:'24px 12px' }}>
              {E?'No sessions yet. Start a new one above.':'Tiada sesi lagi. Mulakan satu di atas.'}
            </p>
          )}

          {todaySessions.length>0 && (
            <>
              <p style={{ fontSize:'10px', fontWeight:700, color:'#4A5568', textTransform:'uppercase', letterSpacing:'0.08em', padding:'8px 6px 4px' }}>{E?'Today':'Hari Ini'}</p>
              {todaySessions.map(s=>(
                <SessionBtn key={s.id} session={s} active={activeSession?.id===s.id} onClick={()=>void openSession(s)} lang={lang}/>
              ))}
            </>
          )}

          {olderSessions.length>0 && (
            <>
              <p style={{ fontSize:'10px', fontWeight:700, color:'#4A5568', textTransform:'uppercase', letterSpacing:'0.08em', padding:'12px 6px 4px' }}>{E?'Earlier':'Terdahulu'}</p>
              {olderSessions.map(s=>(
                <SessionBtn key={s.id} session={s} active={activeSession?.id===s.id} onClick={()=>void openSession(s)} lang={lang}/>
              ))}
            </>
          )}
        </div>

        {/* Usage footer */}
        {usage && (
          <div style={{ padding:'10px 12px', borderTop:'1px solid rgba(255,255,255,0.06)' }}>
            <p style={{ fontSize:'10px', color:'#4A5568', marginBottom:'4px' }}>{E?'Usage today':'Penggunaan hari ini'}</p>
            <div style={{ height:'4px', background:'#1E2128', borderRadius:'999px', overflow:'hidden' }}>
              <div style={{ height:'100%', background:C.royal, width:`${Math.round((usage.daily/usage.dailyLimit)*100)}%`, borderRadius:'999px' }}/>
            </div>
            <p style={{ fontSize:'10px', color:'#4A5568', marginTop:'3px' }}>{usage.daily}/{usage.dailyLimit} {E?'messages':'mesej'}</p>
          </div>
        )}
      </div>

      {/* ── Right: chat panel ── */}
      <div style={{ display:'flex', flexDirection:'column', background:C.card, overflow:'hidden' }}>
        {/* Chat header */}
        <div style={{ padding:'14px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:'10px', flexShrink:0 }}>
          {activeSession ? (
            <>
              <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:C.success, flexShrink:0 }}/>
              <div>
                <p style={{ fontSize:'13px', fontWeight:600, color:C.textPrimary, lineHeight:1.2 }}>
                  {E?'Session active':'Sesi aktif'} · {activeSession.locale.toUpperCase()}
                </p>
                <p style={{ fontSize:'11px', color:C.textSecondary }}>
                  {activeSession.message_count} {E?'messages':'mesej'} · {E?'expires':'tamat'} {new Date(activeSession.expires_at).toLocaleTimeString(E?'en-GB':'ms-BN',{hour:'2-digit',minute:'2-digit'})}
                </p>
              </div>
            </>
          ) : (
            <p style={{ fontSize:'13px', color:C.textSecondary, fontStyle:'italic' }}>
              {E?'Select a session or start a new one →':'Pilih sesi atau mulakan satu baharu →'}
            </p>
          )}
        </div>

        {/* Messages */}
        <div ref={scrollRef} style={{ flex:1, overflowY:'auto', padding:'20px', display:'flex', flexDirection:'column', gap:'16px' }}>

          {/* No session — welcome */}
          {!activeSession && (
            <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', padding:'40px 24px' }}>
              <div style={{ width:'64px', height:'64px', borderRadius:'16px', background:C.royal, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'16px' }}>
                <span style={{ fontFamily:T.font.serif, fontSize:'28px', fontWeight:700, color:C.midnight }}>AI</span>
              </div>
              <h2 style={{ fontFamily:T.font.serif, fontSize:'20px', fontWeight:700, color:C.textPrimary, marginBottom:'8px' }}>
                {E?'Welcome to EduBot':'Selamat datang ke EduBot'}
              </h2>
              <p style={{ fontSize:'13px', color:C.textSecondary, maxWidth:'340px', lineHeight:1.6, marginBottom:'24px' }}>
                {E
                  ?'Your intelligent school data assistant. Ask about attendance, grades, reports, or any SERPS record.'
                  :'Pembantu data sekolah pintar anda. Tanya tentang kehadiran, gred, laporan, atau sebarang rekod SERPS.'}
              </p>

              {/* Suggested questions */}
              <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', justifyContent:'center', maxWidth:'480px', marginBottom:'24px' }}>
                {suggestions.map((s,i)=>(
                  <button
                    key={i}
                    onClick={async()=>{ await startNewSession(); /* session will open, then send */ }}
                    style={{
                      background:'none', border:`1.5px solid ${C.royal}`,
                      borderRadius:'999px', padding:'7px 14px',
                      fontSize:'12px', color:C.textPrimary,
                      cursor:'pointer', fontFamily:T.font.sans,
                      transition:'all 0.1s',
                      textAlign:'left',
                    }}
                    onMouseEnter={e=>{(e.target as HTMLButtonElement).style.background=C.royal;(e.target as HTMLButtonElement).style.color=C.midnight;}}
                    onMouseLeave={e=>{(e.target as HTMLButtonElement).style.background='none';(e.target as HTMLButtonElement).style.color=C.textPrimary;}}
                  >
                    {E?s.en:s.ms}
                  </button>
                ))}
              </div>

              <button
                onClick={()=>void startNewSession()}
                disabled={starting}
                style={{ background:C.royal, color:C.midnight, border:'none', borderRadius:'8px', padding:'11px 24px', fontSize:'14px', fontWeight:700, cursor:'pointer', fontFamily:T.font.sans }}
              >
                {starting?(E?'Starting…':'Memulakan…'):(E?'+ Start Conversation':'+ Mulakan Perbualan')}
              </button>
            </div>
          )}

          {/* Active session — suggestions when empty */}
          {activeSession && messages.length===0 && !sending && (
            <div style={{ textAlign:'center', padding:'24px' }}>
              <p style={{ fontSize:'13px', color:C.textSecondary, marginBottom:'16px' }}>
                {E?'Try one of these:':'Cuba salah satu ini:'}
              </p>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', justifyContent:'center' }}>
                {suggestions.map((s,i)=>(
                  <button
                    key={i}
                    onClick={()=>void handleSend(E?s.en:s.ms)}
                    style={{
                      background:`${C.royal}15`, border:`1.5px solid ${C.royal}44`,
                      borderRadius:'999px', padding:'7px 14px',
                      fontSize:'12px', color:C.textPrimary,
                      cursor:'pointer', fontFamily:T.font.sans,
                    }}
                  >
                    {E?s.en:s.ms}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          {messages.filter(m=>m.role!=='tool').map(m=>(
            <div
              key={m.id}
              style={{
                alignSelf: m.role==='user' ? 'flex-end' : 'flex-start',
                maxWidth:'75%',
                display:'flex', flexDirection:'column',
                gap:'4px',
                alignItems: m.role==='user' ? 'flex-end' : 'flex-start',
              }}
            >
              {m.role==='user' ? (
                <div style={{
                  background:C.royal, color:C.midnight,
                  padding:'10px 14px',
                  borderRadius:'16px 16px 4px 16px',
                  fontSize:'14px', lineHeight:1.5,
                  fontFamily:T.font.sans,
                  whiteSpace:'pre-wrap',
                }}>
                  {m.content}
                </div>
              ) : (
                <>
                  <div style={{ display:'flex', alignItems:'flex-start', gap:'8px' }}>
                    <div style={{ width:'28px', height:'28px', borderRadius:'7px', background:C.midnight, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <span style={{ fontFamily:T.font.serif, fontSize:'11px', fontWeight:700, color:C.royal }}>AI</span>
                    </div>
                    <div style={{
                      background:'#FFFFFF', color:C.textPrimary,
                      padding:'10px 14px',
                      borderRadius:'4px 16px 16px 16px',
                      fontSize:'14px', lineHeight:1.6,
                      border:`1px solid ${C.border}`,
                      fontFamily:T.font.sans,
                      whiteSpace:'pre-wrap',
                      boxShadow:T.shadow.xs,
                    }}>
                      {m.content}
                    </div>
                  </div>
                  {/* Feedback */}
                  <div style={{ display:'flex', gap:'4px', paddingLeft:'36px' }}>
                    {(['POSITIVE','NEGATIVE'] as const).map(fb=>(
                      <button
                        key={fb}
                        onClick={()=>void handleFeedback(m.id,fb)}
                        disabled={m.user_feedback!==null}
                        style={{
                          background:m.user_feedback===fb?`${C.royal}30`:'none',
                          border:'none', cursor:m.user_feedback?'default':'pointer',
                          fontSize:'13px', padding:'2px 4px',
                          borderRadius:'4px', transition:'background 0.1s',
                          opacity:m.user_feedback&&m.user_feedback!==fb?0.3:1,
                        }}
                      >
                        {fb==='POSITIVE'?'👍':'👎'}
                      </button>
                    ))}
                    {m.tokens_used>0 && (
                      <span style={{ fontSize:'10px', color:C.textDisabled, marginLeft:'4px', lineHeight:'24px' }}>
                        {m.tokens_used} tokens
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}

          {/* Thinking indicator */}
          {sending && (
            <div style={{ alignSelf:'flex-start', display:'flex', alignItems:'center', gap:'8px' }}>
              <div style={{ width:'28px', height:'28px', borderRadius:'7px', background:C.midnight, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <span style={{ fontFamily:T.font.serif, fontSize:'11px', fontWeight:700, color:C.royal }}>AI</span>
              </div>
              <div style={{ background:'#FFFFFF', border:`1px solid ${C.border}`, borderRadius:'4px 16px 16px 16px', padding:'10px 14px', boxShadow:T.shadow.xs }}>
                <div style={{ display:'flex', gap:'4px', alignItems:'center' }}>
                  {[0,1,2].map(i=>(
                    <div key={i} style={{ width:'6px', height:'6px', borderRadius:'50%', background:C.textDisabled, animation:`pulse-dot 1.2s ${i*0.2}s ease-in-out infinite` }}/>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Composer ── */}
        {activeSession && (
          <div style={{ padding:'14px 16px', borderTop:`1px solid ${C.border}`, flexShrink:0, background:C.card }}>
            {error && <p style={{ fontSize:'12px', color:C.danger, marginBottom:'8px' }}>{error}</p>}
            <div style={{ display:'flex', gap:'8px', alignItems:'flex-end' }}>
              <textarea
                ref={textRef}
                value={input}
                onChange={e=>setInput(e.target.value)}
                onKeyDown={e=>{ if (e.key==='Enter' && !e.shiftKey){ e.preventDefault(); void handleSend(); } }}
                placeholder={locale==='ms'?'Taip mesej anda… (Shift+Enter untuk baris baharu)':'Type your message… (Shift+Enter for new line)'}
                disabled={!activeSession||sending}
                maxLength={4000}
                rows={2}
                style={{
                  flex:1, background:'#F9FAFB',
                  border:`1.5px solid ${C.border}`,
                  borderRadius:'10px', padding:'10px 14px',
                  fontSize:'14px', color:C.textPrimary,
                  fontFamily:T.font.sans, outline:'none', resize:'none',
                  transition:'border-color 0.1s',
                }}
                onFocus={e=>{ e.target.style.borderColor=C.royal; }}
                onBlur={e=>{ e.target.style.borderColor=C.border; }}
              />
              <button
                onClick={()=>void handleSend()}
                disabled={!activeSession||sending||!input.trim()}
                style={{
                  width:'42px', height:'42px',
                  background: input.trim()&&!sending ? C.royal : C.borderSubtle,
                  color: input.trim()&&!sending ? C.midnight : C.textDisabled,
                  border:'none', borderRadius:'10px',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  cursor:input.trim()&&!sending?'pointer':'not-allowed',
                  transition:'all 0.1s', flexShrink:0,
                }}
              >
                <Send size={16} strokeWidth={2.5}/>
              </button>
            </div>
            <p style={{ fontSize:'11px', color:C.textDisabled, marginTop:'6px' }}>
              {input.length}/4000 · {E
                ?'EduBot can only access data within your role permissions.'
                :'EduBot hanya boleh mengakses data dalam had kebenaran peranan anda.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Session button ────────────────────────────────────────────────────────────

function SessionBtn({ session, active, onClick, lang }:{
  session:Session; active:boolean; onClick:()=>void; lang:string;
}) {
  const E = lang==='en';
  return (
    <button
      onClick={onClick}
      style={{
        width:'100%', display:'flex', alignItems:'center', gap:'8px',
        padding:'9px 10px', background:active?'rgba(247,208,0,0.1)':'transparent',
        border:'none', borderRadius:'7px',
        cursor:'pointer', transition:'background 0.1s',
        textAlign:'left',
      }}
      onMouseEnter={e=>{ if (!active)(e.currentTarget.style.background='rgba(255,255,255,0.04)'); }}
      onMouseLeave={e=>{ if (!active)(e.currentTarget.style.background='transparent'); }}
    >
      <div style={{ width:'28px', height:'28px', borderRadius:'6px', background:active?'rgba(247,208,0,0.15)':'#1E2128', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        <span style={{ fontSize:'10px', fontWeight:700, color:active?'#F7D000':'#4A5568', fontFamily:'Noto Serif, serif' }}>AI</span>
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <p style={{ fontSize:'12px', fontWeight:active?600:400, color:active?'#F9FAFB':'#9CA3AF', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', lineHeight:1.3 }}>
          {session.message_count} {E?'messages':'mesej'} · {session.locale.toUpperCase()}
        </p>
        <p style={{ fontSize:'10px', color:'#4A5568', lineHeight:1.2 }}>
          {new Date(session.started_at).toLocaleTimeString(E?'en-GB':'ms-BN',{hour:'2-digit',minute:'2-digit'})}
        </p>
      </div>
      {active && <ChevronRight size={12} color="#F7D000" style={{ flexShrink:0 }}/>}
    </button>
  );
}
